import { Link } from 'react-router-dom'
import { PixeCard } from '../components/ui/PixeCard'
import { usePublish } from '../hooks/usePublish'
import NavBar from '../components/layout/NavBar'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PublishFilterDrawer from '../components/publish/PublishFilterDrawer'
import type { PublishFilters } from '../types/publish'
import { DEFAULT_PUBLISH_FILTERS } from '../types/publish'
import { resolveLandscapeCover } from '../lib/cover'

export default function Publish() {
  const location = useLocation()
  const navigate = useNavigate()
  const [filters, setFilters] = React.useState<PublishFilters>({ ...DEFAULT_PUBLISH_FILTERS })
  const [filterOpen, setFilterOpen] = React.useState(false)
  const { items } = usePublish(filters)
  const markFreshIntent = () => {
    try { sessionStorage.setItem('publish:fresh', '1') } catch {}
  }
  // Open from NavBar trigger or query param (?filters=1)
  React.useEffect(() => {
    const sp = new URLSearchParams(location.search)
    const q = sp.get('q') || ''
    const mode = sp.get('mode')
    if (q) {
      setFilters((prev) => ({ ...prev, q }))
    } else {
      setFilters((prev) => ({ ...prev, q: '' }))
    }
    if (mode === 'saved' || mode === 'all') {
      setFilters((prev) => ({ ...prev, mode }))
    } else {
      setFilters((prev) => ({ ...prev, mode: 'all' }))
    }
    if (sp.get('filters') === '1') {
      setFilterOpen(true)
      // Scroll to grid so user stays on pixes, not at hero
      setTimeout(() => {
        try { document.getElementById('inspo')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) } catch {}
      }, 0)
    }
  }, [location.search])
  React.useEffect(() => {
    const open = () => setFilterOpen(true)
    const scrollToGrid = () => {
      try { document.getElementById('inspo')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) } catch {}
    }
    window.addEventListener('pixeloop:publishFiltersOpen' as any, open)
    window.addEventListener('pixeloop:publishScrollToGrid' as any, scrollToGrid)
    return () => {
      window.removeEventListener('pixeloop:publishFiltersOpen' as any, open)
      window.removeEventListener('pixeloop:publishScrollToGrid' as any, scrollToGrid)
    }
  }, [])
  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-10">
        <div className="mb-4">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1)
              else navigate('/publish')
            }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/85 hover:bg-white/10"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
              <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            Back to Publish
          </button>
        </div>
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(80%_80%_at_10%_-10%,rgba(99,102,241,0.25),transparent_60%)] p-8">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Create a Pixe</h1>
          <p className="mt-2 text-white/85 max-w-2xl">A visual story you can read, share, and collect - cover art, title, subtitle, and rich text that looks great anywhere.</p>
          <div className="mt-6 flex items-center gap-3">
            <Link to="/publish/new?fresh=1" onClick={markFreshIntent} className="inline-flex items-center rounded-xl bg-brand px-5 py-3 font-semibold shadow hover:bg-brand-600">Start New Pixe</Link>
            <Link
              to="/settings/become-a-creator"
              className="inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 font-semibold text-white/90 hover:bg-white/10"
            >
              Become a Creator
            </Link>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-white/80">
            <li className="glass rounded-lg p-3">Full-bleed cover in portrait and landscape</li>
            <li className="glass rounded-lg p-3">Clean reading experience on web and mobile</li>
            <li className="glass rounded-lg p-3">Images, quotes, and sections for long-form</li>
            <li className="glass rounded-lg p-3">One link to share anywhere</li>
          </ul>
        </section>

        {/*
        <section id="templates" className="mt-10">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">Templates</h2>
            <Link to="/publish/new?fresh=1" className="text-sm text-white/80 hover:text-white">Open blank</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <TemplateCard label="Micro" desc="Cover + quick note" to="/publish/new?fresh=1&template=micro"/>
            <TemplateCard label="Zine" desc="Sections + images" to="/publish/new?fresh=1&template=zine"/>
            <TemplateCard label="Book" desc="Chapters for long-form" to="/publish/new?fresh=1&template=book"/>
            <TemplateCard label="Photo Set" desc="Gallery with captions" to="/publish/new?fresh=1&template=photo"/>
            <TemplateCard label="Launch" desc="Cover + CTA block" to="/publish/new?fresh=1&template=launch"/>
          </div>
        </section>
        */}

        {/* Specs */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <Spec title="Cover images">
            Use 16:9 and 8:9. Minimum 1600px. JPG/PNG/WebP.
          </Spec>
          <Spec title="Writing">
            Clear title, short subtitle. Use headings, quotes, and captions.
          </Spec>
          <Spec title="Accessibility">
            Add alt text for images; keep contrast high; avoid text in images.
          </Spec>
        </section>

        {/* Inspiration (popular) */}
        {items && items.length > 0 && (
          <section id="inspo" className="mt-10">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-extrabold">Inspiration</h2>
                <div className="text-xs text-white/70">Sorted by {filters.sort}</div>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/" className="text-sm text-white/80 hover:text-white">Explore Pixe Publish</Link>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.slice(0, 8).map((it, i) => (
                <PixeCard
                  key={it?.id ?? i}
                  title={it?.format === 'job' && (it as any)?.job?.title ? (it as any).job.title : (it?.title || it?.user?.displayName)}
                  subtitle={it?.format === 'job' && (it as any)?.job?.summary ? (it as any).job.summary : (it?.subtitle || it?.user?.handle)}
                  image={resolveLandscapeCover(it)}
                  href={it?.format === 'job' && (it as any)?.job?.id ? `/jobs/${(it as any).job.id}` : `/p/${it?.id}`}
                />
              ))}
            </div>
          </section>
        )}
      </main>
      {/* Right drawer filters */}
      <PublishFilterDrawer
        open={filterOpen}
        onClose={() => {
          setFilterOpen(false)
          const sp = new URLSearchParams(location.search)
          if (sp.get('filters')) {
            sp.delete('filters')
            navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true })
          }
        }}
        filters={filters}
        onApply={(next) => {
          setFilters(next)
          setFilterOpen(false)
          const sp = new URLSearchParams(location.search)
          if (sp.get('filters')) {
            sp.delete('filters')
            navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true })
          }
        }}
        onClear={() => setFilters({ ...DEFAULT_PUBLISH_FILTERS })}
      />
    </div>
  )
}

function TemplateCard({ label, desc, to }: { label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="group rounded-2xl border border-white/10 p-4 hover:bg-white/5 transition">
      <div className="h-32 rounded-lg bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent)]" />
      <div className="mt-3 font-semibold">{label}</div>
      <div className="text-sm text-white/70">{desc}</div>
      <div className="mt-3 inline-flex items-center text-sm text-brand-400 group-hover:underline">Use template</div>
    </Link>
  )
}

function Spec({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4">
      <div className="text-white font-semibold mb-1">{title}</div>
      <div className="text-white/80">{children}</div>
    </div>
  )
}
